import React, {useMemo} from 'react';

import {SmallLoader} from '@kit';

import {getApolloError} from '../../../components/utils';
import {CharacterContextValue, CharacterProvider} from './character-context';
import {useCatalog} from './data/use-catalog';
import {useEquipmentItems} from './data/use-equipment-items';
import {useSheetEditor} from './data/use-sheet-editor';
import {Character} from './model/character';
import type {CharacterState} from './model/character-state';
import {buildCharacterFeatures} from './model/feature-tree';
import {SaveIndicator} from './save-indicator';

import css from './character-page.module.css';

type Props = {
	/** лист уже есть в базе */
	sheetId?: string;
	initialState?: CharacterState;
	/** лист создан в базе после первого изменения */
	onCreated?(sheetId: string): void;
	children: React.ReactNode;
};

export const CharacterEditor: React.FC<Props> = ({sheetId, initialState, onCreated, children}) => {
	const {catalog, error} = useCatalog();
	const editor = useSheetEditor({sheetId, initialState, onCreated});
	const {state, update, setChoice, saveStatus} = editor;
	const equipmentItems = useEquipmentItems(state.equipment);

	const contextValue = useMemo((): CharacterContextValue | undefined => {
		if (!catalog) return undefined;
		const features = buildCharacterFeatures(state, catalog);
		const character = new Character(state, features, catalog, equipmentItems);
		return {catalog, state, features, character, sheetId: editor.sheetId, saveStatus, update, setChoice};
	}, [catalog, state, equipmentItems, editor.sheetId, saveStatus, update, setChoice]);

	if (error) return <div className={css.message}>{getApolloError(error).message}</div>;
	if (!contextValue) {
		return (
			<div className={css.message}>
				<SmallLoader />
			</div>
		);
	}
	return (
		<CharacterProvider value={contextValue}>
			{children}
			<SaveIndicator />
		</CharacterProvider>
	);
};
